import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ACTIONS, ActionKey, ActionVariant, ICON_SIZE, IconComponent } from './actions';
import { ConfirmOptions, useConfirm } from '../../contexts/ConfirmContext';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { cx } from './cx';

const DANGER_ACTIONS: ActionKey[] = ['delete', 'remove', 'reject'];

// Classes literais (não montar com template): o Tailwind só mantém no CSS as classes de
// @layer components que aparecem escritas no código.
const TONE_CLASS: Record<ActionVariant, string> = {
  default: '',
  primary: 'row-action--primary',
  success: 'row-action--success',
  warning: 'row-action--warning',
  danger: 'row-action--danger',
};

export interface ActionButtonProps {
  /** Define ícone, cor e rótulo padrão (ver ACTIONS em ./actions). Sem `action`, passe `icon` + `label`. */
  action?: ActionKey;
  /** Rótulo específico ("Excluir lead"); padrão: t(`ui.actions.<action>`). */
  label?: string;
  /** Só para casos sem ação equivalente no registro — prefira adicionar ao registro. */
  icon?: IconComponent;
  variant?: ActionVariant;
  onClick?: (event: React.MouseEvent) => unknown;
  /** Rota interna (react-router). */
  to?: string;
  /** URL externa: abre em nova aba (ou baixa, com `download`). */
  href?: string;
  download?: boolean | string;
  /** Texto copiado por `copy`/`copyLink`. */
  value?: string;
  copyMessage?: string | false;
  /** Pede confirmação (ConfirmDialog) antes do onClick. String = título. */
  confirm?: string | ConfirmOptions;
  disabled?: boolean;
  loading?: boolean;
  /** 'mobile' (padrão): rótulo só no celular, ícone com tooltip no desktop. 'always': rótulo também no desktop. */
  labelVisibility?: 'mobile' | 'always';
  /** aria-expanded, para expandir/recolher. */
  expanded?: boolean;
  className?: string;
}

/**
 * Ação de item de lista/card (editar, excluir, copiar, abrir…). No mobile vira botão grande com
 * rótulo dentro de <RowActions>; no desktop, ícone compacto com title/aria-label.
 */
const ActionButton: React.FC<ActionButtonProps> = ({
  action,
  label,
  icon,
  variant,
  onClick,
  to,
  href,
  download,
  value,
  copyMessage,
  confirm: confirmProp,
  disabled,
  loading,
  labelVisibility = 'mobile',
  expanded,
  className,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { copy, isCopied } = useCopyToClipboard();
  const [busy, setBusy] = useState(false);

  const def = action ? ACTIONS[action] : undefined;
  const isCopy = action === 'copy' || action === 'copyLink';
  const copied = isCopy && isCopied();
  const defaultLabel = action ? t(`ui.actions.${action}`) : '';
  const text = copied ? t('ui.copied') : label || defaultLabel;
  const Icon = copied ? Check : loading || busy ? Loader2 : icon || def?.icon || MoreHorizontal;
  const tone = copied ? 'success' : variant || def?.variant || 'default';

  const classes = cx(
    'row-action',
    TONE_CLASS[tone],
    labelVisibility === 'always' && 'md:gap-1.5 md:px-2.5',
    className
  );

  const content = (
    <>
      <Icon className={cx(ICON_SIZE.row, (loading || busy) && !copied && 'animate-spin')} aria-hidden="true" />
      <span className={labelVisibility === 'always' ? undefined : 'md:hidden'}>{text}</span>
    </>
  );

  const common = { className: classes, title: text, 'aria-label': text };

  if (to && !disabled) {
    return (
      <Link to={to} {...common} onClick={onClick}>
        {content}
      </Link>
    );
  }

  if (href && !disabled) {
    return (
      <a
        href={href}
        {...common}
        onClick={onClick}
        {...(download
          ? { download: typeof download === 'string' ? download : '' }
          : { target: '_blank', rel: 'noopener noreferrer' })}
      >
        {content}
      </a>
    );
  }

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (confirmProp) {
      const opts: ConfirmOptions =
        typeof confirmProp === 'string' ? { title: confirmProp } : confirmProp;
      const isDanger = !!action && DANGER_ACTIONS.includes(action);
      const ok = await confirm({
        confirmText: label || defaultLabel || undefined,
        variant: isDanger ? 'danger' : 'default',
        message: action === 'delete' ? t('ui.confirmDelete') : undefined,
        ...opts,
      });
      if (!ok) return;
    }
    if (isCopy && value !== undefined) {
      await copy(value, 'default', { message: copyMessage });
    }
    if (onClick) {
      const result = onClick(event);
      if (result instanceof Promise) {
        setBusy(true);
        try {
          await result;
        } finally {
          setBusy(false);
        }
      }
    }
  };

  return (
    <button
      type="button"
      {...common}
      aria-expanded={expanded}
      onClick={handleClick}
      disabled={disabled || loading || busy}
    >
      {content}
    </button>
  );
};

export default ActionButton;
